import { useState, useEffect } from "react";
import { X, Film, Camera, AlertTriangle } from "lucide-react";

export default function MovieStarConsentModal({ open, onClose, onAgree, busy }) {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { if (!open) setConfirmed(false); }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(23,20,38,.62)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="moviestar-consent-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-[24px] relative border-2 border-[#5d8a52] max-h-[92vh] flex flex-col"
        style={{ boxShadow: "0 30px 80px -24px rgba(23,20,38,.6)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="moviestar-consent-close"
          onClick={onClose}
          className="absolute top-3.5 right-3.5 p-2 rounded-full text-[#9a96ac] hover:bg-[#f1f0f5] transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="px-6 pt-6 pb-3">
          <div className="flex items-center gap-2 text-[#5d8a52] font-bold text-sm">
            <Film size={16} /> MOVIESTAR MODE — TERMS
          </div>
          <h2 className="text-xl font-extrabold mt-1.5 leading-tight">Likeness &amp; consent</h2>
          <p className="text-sm text-[#6b6880] mt-1">Please read this carefully. You'll agree to it every time.</p>
          <p className="text-[11px] font-semibold text-[#9a96ac] mt-1" data-testid="moviestar-terms-version">Terms v2026-07-19</p>
        </div>

        <div className="px-6 overflow-y-auto flex-1" data-testid="moviestar-legal">
          {/* Photo instructions */}
          <div className="rounded-2xl bg-[#f6f3ff] border border-[#e7efe1] p-4 mb-3">
            <div className="flex items-center gap-1.5 font-bold text-[#4a475c] text-sm mb-1">
              <Camera size={15} className="text-[#5d8a52]" /> For the best result
            </div>
            <p className="text-xs text-[#6b6880] leading-relaxed">
              Upload a <span className="font-semibold">clear, well-lit photo of your face</span> — head and shoulders,
              looking toward the camera. A picture of your <span className="font-semibold">face, not your whole body</span>,
              works best.
            </p>
          </div>

          {/* AI caveat */}
          <div className="rounded-2xl bg-[#fff7ed] border border-[#fde3c2] p-4 mb-4">
            <div className="flex items-center gap-1.5 font-bold text-[#9a5b12] text-sm mb-1">
              <AlertTriangle size={15} /> This is AI — not a perfect copy
            </div>
            <p className="text-xs text-[#9a5b12] leading-relaxed">
              Because this uses artificial intelligence, an <span className="font-semibold">exact replica of your
              likeness isn't always guaranteed</span>. Results are a fun, stylized approximation and may not perfectly
              match your real appearance.
            </p>
          </div>

          <div className="text-xs text-[#6b6880] leading-relaxed space-y-2.5 pb-2">
            <p>
              MovieStar Mode lets our AI feature your likeness performing the steps of your manual and video. It is an
              entertainment novelty, offered for personal, lawful, family-friendly use only.
            </p>
            <p className="font-semibold text-[#4a475c]">By continuing you confirm and agree that:</p>
            <ul className="list-disc pl-4 space-y-1.5">
              <li>The photo is of <span className="font-semibold">yourself</span>, or of an adult who has given you their
                explicit, informed permission to use their likeness this way. Never upload a photo of someone else without
                their consent.</li>
              <li>You will <span className="font-semibold">not upload photos of children or minors</span> under any
                circumstances.</li>
              <li>You will not upload photos of public figures, celebrities, or anyone you do not have the right to use,
                and you will keep all content lawful, wholesome and family-friendly.</li>
              <li>To create your images, your photo is transmitted to third-party AI providers —
                <span className="font-semibold"> Google Gemini</span> (images) and <span className="font-semibold">OpenAI</span> (text).
                Their handling of data is governed by their own policies (
                <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#5d8a52] underline" data-testid="moviestar-gemini-tos">Gemini terms</a>
                {" · "}
                <a href="https://openai.com/policies/terms-of-use" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#5d8a52] underline" data-testid="moviestar-openai-tos">OpenAI terms</a>
                ), which Vittyyo does not control.</li>
              <li>You are solely responsible for the photos you upload and the images you create, download, or share
                elsewhere.</li>
            </ul>
            <p>
              <span className="font-semibold text-[#4a475c]">Your photo is never stored.</span> We use it once to generate
              this manual and then discard it from our servers — we do not save it, sell it, or make it public. However,
              we <span className="font-semibold">cannot</span> delete copies held by the AI providers we transmit it to,
              nor anything you download or post to other platforms.
            </p>
            <p>
              Vittyyo provides this tool <span className="font-semibold">"as is,"</span> without warranties of any kind.
              To the fullest extent permitted by law, Vittyyo disclaims all liability for uploaded content, generated
              results, and content you share to other platforms.
            </p>
            <p>
              <span className="font-semibold text-[#4a475c]">Release of liability on download.</span> Once you download,
              export, or save any content you create with Vittyyo — including videos, images, and written documents —
              you <span className="font-semibold">release Vittyyo from any and all liability</span> relating to that
              content. We are <span className="font-semibold">not responsible for how you use it or where you upload,
              publish, or share it</span>. By downloading any content you create with our service, you acknowledge and
              agree to all of the terms in this disclaimer.
            </p>
            <p>
              By clicking "I Agree" you accept these terms and confirm you are at least 18 years old. A
              <span className="font-semibold"> timestamped record of your agreement</span> to this document is stored on
              our servers. Misuse of this feature — including uploading a photo of a minor or of anyone without their
              consent — may result in <span className="font-semibold">termination of your account</span>.
            </p>
          </div>
        </div>

        <div className="px-6 pt-3">
          <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="moviestar-age-confirm-label">
            <input
              data-testid="moviestar-age-confirm"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-[#4f7746] shrink-0 cursor-pointer"
            />
            <span className="text-xs text-[#4a475c] font-semibold leading-relaxed">
              I am 18 years of age or older, and I have the right to use this photo.
            </span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-[#f0eef7] flex gap-3">
          <button
            data-testid="moviestar-consent-cancel"
            onClick={onClose}
            className="flex-1 py-3 rounded-2xl font-bold text-[#6b6880] bg-[#f1f0f5] hover:bg-[#e8e6ef] transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="moviestar-consent-agree"
            onClick={onAgree}
            disabled={busy || !confirmed}
            className="flex-1 py-3 rounded-2xl font-bold text-white bg-[#4f7746] hover:bg-[#3f6238] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "One sec…" : "I Agree"}
          </button>
        </div>
      </div>
    </div>
  );
}
